import type { ReactNode } from "react";
import { Loader2, Package } from "lucide-react";
import { Badge } from "@project/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import { resolveMediaUrl } from "@project/lib/utils";
import type { Product, ProductVariant } from "@project/types/gruh-udhyog";

function formatMoney(value: string | number | null | undefined) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return "—";
  }
  return `₹${n.toFixed(2)}`;
}

function formatDate(value?: string) {
  if (!value) {
    return "—";
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return value;
  }
  return d.toLocaleString("en-GB");
}

export function variantLabel(v: ProductVariant) {
  return v.value ?? v.weight ?? "—";
}

export function variantDisplayPrice(v: ProductVariant) {
  return v.display_price ?? v.discounted_price ?? v.price;
}

export function productPriceRange(variants?: ProductVariant[]) {
  if (!variants?.length) {
    return null;
  }
  const prices = variants
    .map(v => Number(variantDisplayPrice(v)))
    .filter(n => Number.isFinite(n));
  if (!prices.length) {
    return null;
  }
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min === max) {
    return formatMoney(min);
  }
  return `${formatMoney(min)} – ${formatMoney(max)}`;
}

export function productTotalStock(variants?: ProductVariant[]) {
  if (!variants?.length) {
    return null;
  }
  return variants.reduce(
    (sum, v) =>
      sum + Number(v.available_stock ?? v.total_stock ?? v.stock ?? 0),
    0
  );
}

type ProductViewDialogProps = {
  product: Product | null;
  loading?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function DetailItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <div className="mt-0.5 text-sm font-medium text-foreground">{value}</div>
    </div>
  );
}

export function ProductViewDialog({
  product,
  loading = false,
  open,
  onOpenChange,
}: ProductViewDialogProps) {
  const categoryName =
    product?.category_name ??
    product?.category_details?.category_name ??
    (product?.category != null ? `#${product.category}` : "—");

  const variantMasterTitle =
    product?.variant_master_details?.title ??
    (product?.variant_master != null ? `#${product.variant_master}` : "—");

  const imageSrc = resolveMediaUrl(
    product?.image ?? product?.image_url ?? null
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col gap-0 overflow-hidden rounded-2xl p-0">
        <DialogHeader className="shrink-0 border-b border-border px-6 py-4">
          <DialogTitle className="text-lg font-bold">
            Product Details
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto px-6 py-5">
          {loading || !product ? (
            <div className="flex h-48 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-5 text-sm">
              <div className="flex flex-col gap-4 sm:flex-row">
                <div className="from-primary/5 flex h-36 w-full shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-gradient-to-br via-amber-100/10 to-emerald-100/10 sm:w-44">
                  {imageSrc ? (
                    <img
                      src={imageSrc}
                      alt={product.product_name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Package className="text-primary/40 h-12 w-12" />
                  )}
                </div>

                <div className="min-w-0 flex-1 space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-xl font-bold text-foreground">
                      {product.product_name}
                    </h3>
                    <Badge
                      variant={product.is_active ? "outline" : "secondary"}
                    >
                      {product.is_active ? "Active" : "Inactive"}
                    </Badge>
                    {product.is_handmade && (
                      <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">
                        Handmade
                      </Badge>
                    )}
                    {product.is_signature && (
                      <Badge className="bg-amber-500 text-white hover:bg-amber-500">
                        Signature
                      </Badge>
                    )}
                    {product.is_discountable != null && (
                      <Badge variant="secondary">
                        {product.is_discountable
                          ? "Discountable"
                          : "Not discountable"}
                      </Badge>
                    )}
                  </div>

                  <div className="bg-muted/30 border-border/60 grid gap-3 rounded-xl border p-3 sm:grid-cols-2">
                    <DetailItem label="Category" value={categoryName} />
                    <DetailItem
                      label="Variant Master"
                      value={variantMasterTitle}
                    />
                    <DetailItem label="Product ID" value={product.id} />
                    <DetailItem
                      label="Variants"
                      value={product.variants?.length ?? 0}
                    />
                    <DetailItem
                      label="Created"
                      value={formatDate(product.created_at)}
                    />
                    <DetailItem
                      label="Updated"
                      value={formatDate(product.updated_at)}
                    />
                  </div>
                </div>
              </div>

              {(product.short_description || product.description) && (
                <div className="space-y-3">
                  {product.short_description && (
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        Short Description
                      </p>
                      <p className="mt-1 text-muted-foreground">
                        {product.short_description}
                      </p>
                    </div>
                  )}
                  {product.description && (
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        Description
                      </p>
                      <p className="mt-1 whitespace-pre-wrap text-muted-foreground">
                        {product.description}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div>
                <p className="mb-2 text-sm font-semibold text-foreground">
                  Variants
                </p>
                {!product.variants?.length ? (
                  <p className="text-xs text-muted-foreground">
                    No variants found for this product.
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-border">
                    <Table>
                      <TableHeader>
                        <TableRow className="hover:bg-transparent">
                          <TableHead>Value</TableHead>
                          <TableHead>SKU</TableHead>
                          <TableHead>Price</TableHead>
                          <TableHead>Discounted</TableHead>
                          <TableHead>Display</TableHead>
                          <TableHead>Tax %</TableHead>
                          <TableHead>Total Stock</TableHead>
                          <TableHead>Reserved</TableHead>
                          <TableHead>Available</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {product.variants.map(v => (
                          <TableRow key={v.id}>
                            <TableCell className="font-medium">
                              {variantLabel(v)}
                            </TableCell>
                            <TableCell>{v.sku || "—"}</TableCell>
                            <TableCell>{formatMoney(v.price)}</TableCell>
                            <TableCell>
                              {v.discounted_price
                                ? formatMoney(v.discounted_price)
                                : "—"}
                            </TableCell>
                            <TableCell>
                              {formatMoney(variantDisplayPrice(v))}
                            </TableCell>
                            <TableCell>{v.tax ?? "—"}</TableCell>
                            <TableCell>
                              {v.total_stock ?? v.stock ?? "—"}
                            </TableCell>
                            <TableCell>{v.reserved_stock ?? "—"}</TableCell>
                            <TableCell>
                              {v.available_stock ?? v.stock ?? "—"}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  v.is_active !== false
                                    ? "outline"
                                    : "secondary"
                                }
                              >
                                {v.is_active !== false ? "Active" : "Inactive"}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
